import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader, StatusBadge } from "@/components/bits";
import api, { fmtDate } from "@/lib/api";

export default function MyLearning() {
  const [enrollments, setEnrollments] = useState(null);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    api.get("/candidate/enrollments").then(({ data }) => setEnrollments(data.enrollments)).catch(() => setEnrollments([]));
    api.get("/candidate/certificates").then(({ data }) => setCertificates(data.certificates)).catch(() => {});
  }, []);

  if (!enrollments) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="my-learning-page">
      <PageHeader title="My Learning" description="Courses you are enrolled in and certificates issued by training institutes." />

      {enrollments.length === 0 ? (
        <EmptyState
          title="No courses yet"
          description="Browse the learning marketplace — courses recommended for your role and skill gaps."
          cta="Browse Courses"
          onCta={() => (window.location.href = "/courses")}
          testId="learning-empty"
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          {enrollments.map((e) => (
            <div key={e.id} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`enrollment-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-semibold text-[#0B1B3D]">{e.course_title}</p>
                  <p className="text-xs text-muted-foreground mt-1">Enrolled {fmtDate(e.created_at)}</p>
                </div>
                <StatusBadge status={e.status} />
              </div>
              {e.certificate && (
                <p className="text-xs mt-4 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800 flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5" /> Certificate {e.certificate.cert_id} issued by {e.certificate.institute_name}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {certificates.length > 0 && (
        <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="certificates-card">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Certificates</h3>
          <div className="space-y-3">
            {certificates.map((c) => (
              <div key={c.id} className="border border-border/60 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`cert-${c.cert_id}`}>
                <div>
                  <p className="font-semibold text-sm">{c.course_title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {c.cert_id} · Issued by {c.institute_name} on {fmtDate(c.issue_date)}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">Verification: /api/public/certificates/verify/{c.cert_id}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to="/courses">
        <Button variant="outline" className="rounded-full mt-8" data-testid="browse-courses-btn">
          <BookOpen className="w-4 h-4 mr-1.5" /> Browse more courses
        </Button>
      </Link>
    </div>
  );
}
